import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import api from "../../api";

export default function PhotoEvidence({
  question,
  assignment,
  photos = [],
  editable,
  busy,
  onBusy,
  onConfirmed,
  onPending,
}) {
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);
  const [image, setImage] = useState(null);
  useEffect(
    () => () => {
      if (image) URL.revokeObjectURL(image);
    },
    [image],
  );
  const current = assignment.evidence?.[question.id];
  const history = photos.filter((p) => p.question_id === question.id);
  const upload = async (candidate) => {
    setError("");
    setProgress(0);
    onBusy(true);
    try {
      const { data } = await api({
        method: "put",
        url: `/surveys/assignments/${assignment.id}/photos/${question.id}/${candidate.id}`,
        params: { expected_revision: candidate.revision },
        data: candidate.file,
        headers: {
          "Content-Type": candidate.file.type || "application/octet-stream",
        },
        onUploadProgress: (e) =>
          setProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0),
      });
      onConfirmed(data);
      setPending(null);
      onPending(false);
    } catch (e) {
      setError(
        typeof e.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Upload not confirmed. Retry the same photo; your file is still selected.",
      );
    } finally {
      setProgress(null);
      onBusy(false);
    }
  };
  const view = async (photo) => {
    setError("");
    onBusy(true);
    try {
      const { data } = await api({
        method: "get",
        url: `/surveys/assignments/${assignment.id}/photos/${photo.uuid}`,
        responseType: "blob",
      });
      setImage(URL.createObjectURL(data));
    } catch (_) {
      setError("Photo could not be loaded. Try again.");
    } finally {
      onBusy(false);
    }
  };
  return (
    <Box sx={{ borderLeft: "3px solid #a8c5d8", pl: 2, mt: 1 }}>
      <Typography variant="body2">
        {question.photo_required ? "Photo required" : "Optional photo"} ·
        JPEG/PNG, up to 8 MiB and 24 megapixels
      </Typography>
      <Typography variant="caption" display="block">
        {current ? "Photo confirmed by server." : "No confirmed photo."}{" "}
        Originals are visible to the assigned shopper and survey administrators.
      </Typography>
      {history.map((p) => (
        <Button
          key={p.uuid}
          size="small"
          disabled={busy}
          onClick={() => view(p)}
        >
          {p.uuid === current ? "View current photo" : "View earlier photo"} ·{" "}
          {new Date(p.created_at).toLocaleString()}
        </Button>
      ))}
      {editable && (
        <Stack direction="row" spacing={1} sx={{ my: 1 }}>
          <Button component="label" disabled={busy} size="small">
            {current ? "Replace photo" : "Add photo"}
            <input
              hidden
              type="file"
              accept="image/jpeg,image/png"
              aria-label={`Photo for ${question.label}`}
              disabled={busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                if (file.size > 8 * 1024 * 1024 || !file.size) {
                  setError("Choose a nonempty photo up to 8 MiB.");
                  return;
                }
                const candidate = {
                  file,
                  id: crypto.randomUUID(),
                  revision: assignment.revision,
                };
                setPending(candidate);
                onPending(true);
                upload(candidate);
              }}
            />
          </Button>
          {pending && (
            <Button disabled={busy} onClick={() => upload(pending)}>
              Retry same photo
            </Button>
          )}
        </Stack>
      )}
      {pending && progress === null && (
        <Button
          disabled={busy}
          onClick={() => {
            setPending(null);
            setError("");
            onPending(false);
          }}
        >
          Discard local selection
        </Button>
      )}
      {progress !== null && (
        <>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption">
            {progress < 100
              ? `Uploading ${progress}%`
              : "Verifying stored photo…"}
          </Typography>
        </>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {pending && progress === null && (
        <Typography variant="caption">
          {pending.file.name} is not confirmed. A refresh clears this local
          selection; reopen the assignment to check for a server-confirmed
          photo.
        </Typography>
      )}
      <Dialog
        open={!!image}
        onClose={() => setImage(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Original photo · {question.label}</DialogTitle>
        <DialogContent>
          <img
            src={image || undefined}
            alt={`Evidence for ${question.label}`}
            style={{ maxWidth: "100%" }}
          />
          <Button onClick={() => setImage(null)}>Close photo</Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
